import { setContext } from "@apollo/client/link/context";
import { SmokeballAuthenticator } from "./smokeball-authenticator";
import { ApolloClient, InMemoryCache, createHttpLink, gql } from "@apollo/client";
import { filterFalsy } from "../helpers/TsHelpers";
import { Maybe } from "graphql/jsutils/Maybe";
import { mapMatterFromGQL } from "../models/matter-model";
import { ensureConfig } from "./configuration-service";

const makeCollectionResponse = <T>(collection: T[] | null, error: Maybe<string>) => ({
  hasError: !!error,
  error: error,
  matters: collection || [],
});

const makeResponse = <T>(thing: T | null, error: Maybe<string>) => ({
  hasError: !!error,
  error: error,
  matter: thing || null,
});

let client: ApolloClient<object> | null = null;

const getClient = () => {
  if (!client) {
    const regionConfig = ensureConfig();
    const uri = regionConfig.MatterManagementGraphQLUrl;

    const httpLink = createHttpLink({
      uri: uri,
    });

    const authLink = setContext(async (_, { headers }) => {
      const authenticator = SmokeballAuthenticator.getSmokeballAuthenticator();
      const token = await authenticator.getTokenFromStorage();
      const newLocal = `Bearer ${token?.idtoken}`;

      return {
        headers: {
          ...headers,
          authorization: newLocal,
        },
      };
    });

    client = new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }

  return client;
};

export type TSearchFlags = "INCLUDE_CLOSED";

const myRecentMatters = async () => {
  // This query needs to be replaced with the real recent matters query when the server endpoint is available.
  const recentMattersQuery = gql`
    query GetMattersWithRecentActivityForUser($reverseOrder: Boolean!, $skip: Int, $take: Int) {
      mattersWithRecentActivityForUser(reverseOrder: $reverseOrder, skip: $skip, take: $take) {
        items {
          id
          isLead
          status
          matterTypeId
          matterType {
            name
          }
          description
          matterNumber
          clientDisplay
          clients {
            name
          }
          dateOpened
          attorneyResponsibleId
          attorneyResponsible {
            id
            initials
            isFormerStaff
            firstName
            lastName
            colorFill
            colorStroke
          }
          personAssistingId
          personAssisting {
            id
            initials
            isFormerStaff
            firstName
            lastName
            colorFill
            colorStroke
          }
        }
      }
    }
  `;

  try {
    const result = await getClient().query({
      query: recentMattersQuery,
      variables: {
        reverseOrder: false,
        skip: 0,
        take: 50,
      },
      fetchPolicy: "cache-first",
    });

    if (result.error) {
      console.error(result.error);
      return makeCollectionResponse([], JSON.stringify(result.error));
    }

    const rawMatters = (result.data.mattersWithRecentActivityForUser.items as unknown[]) || [];

    const matters = rawMatters.map(mapMatterFromGQL).filter(filterFalsy);

    return makeCollectionResponse(matters, null);
  } catch (error) {
    console.error(error);

    return makeCollectionResponse([], JSON.stringify(error));
  }
};

const getMatterById = async (matterId: string) => {
  if (!matterId || matterId == null) {
    return makeResponse(null, null);
  }

  try {
    const response = await getMattersById([matterId]);

    if (response.error) {
      return makeResponse(null, JSON.stringify(response.error));
    }

    return makeResponse(response.matters[0], null);
  } catch (error) {
    console.error(error);

    return makeResponse(null, JSON.stringify(error));
  }
};

const getMattersById = async (matterIds: Array<string>) => {
  if (!matterIds || matterIds == null || matterIds.length == 0) {
    return makeCollectionResponse([], null);
  }

  const getMatterByIdsQuery = gql`
    query GetMattersByIds($ids: [ID]) {
      mattersById(ids: $ids) {
        id
        isLead
        status
        matterTypeId
        matterType {
          name
        }
        description
        matterNumber
        clientDisplay
        clients {
          name
        }
        dateOpened
        notFound
        attorneyResponsibleId
        attorneyResponsible {
          id
          initials
          isFormerStaff
          firstName
          lastName
          colorFill
          colorStroke
        }
        personAssistingId
        personAssisting {
          id
          initials
          isFormerStaff
          firstName
          lastName
          colorFill
          colorStroke
        }
      }
    }
  `;

  try {
    const result = await getClient().query({
      query: getMatterByIdsQuery,
      variables: {
        ids: matterIds,
      },
    });

    if (result.error) {
      console.error(result.error);
      return makeCollectionResponse([], JSON.stringify(result.error));
    }

    const rawMatters = result.data.mattersById as unknown[];

    const matters = rawMatters.map(mapMatterFromGQL).filter(filterFalsy);

    return makeCollectionResponse(matters, null);
  } catch (error) {
    console.error(error);

    return makeCollectionResponse([], JSON.stringify(error));
  }
};

type TMatterStatus = "OPEN" | "PENDING" | "CLOSED" | "DELETED" | "CANCELLED";

const getMatterStatusFlagsSearchFlags = (searchFlags?: TSearchFlags[]): TMatterStatus[] => {
  let statuses: TMatterStatus[] = ["OPEN", "PENDING"];

  for (const flag of searchFlags || []) {
    switch (flag) {
      case "INCLUDE_CLOSED": {
        statuses.push("CLOSED");
        statuses.push("DELETED");
        statuses.push("CANCELLED");
        break;
      }
    }
  }

  return statuses;
};

const search = async (searchText: string | null | undefined, searchFlags?: TSearchFlags[]) => {
  if (!searchText || searchText == null) {
    return makeCollectionResponse([], null);
  }

  const searchQuery = gql`
    query SearchMatters($query: MatterSearchInput!, $take: Int) {
      searchMatters(query: $query, take: $take) {
        items {
          id
          isLead
          status
          matterTypeId
          matterType {
            name
          }
          description
          matterNumber
          clientDisplay
          clients {
            name
          }
          dateOpened
          attorneyResponsibleId
          attorneyResponsible {
            id
            initials
            isFormerStaff
            firstName
            lastName
            colorFill
            colorStroke
          }
          personAssisting {
            id
            initials
            isFormerStaff
            firstName
            lastName
            colorFill
            colorStroke
          }
          searchMetaData {
            id
            score
            lastUpdated
            explanation {
              value
              score
              description
            }
            fieldHighlights {
              field
              content
            }
          }
        }
        totalCount
      }
    }
  `;

  const statuses = getMatterStatusFlagsSearchFlags(searchFlags);

  try {
    const result = await getClient().query({
      query: searchQuery,
      variables: {
        query: {
          searchQuery: searchText,
          statuses,
        },
        take: 100,
      },
      fetchPolicy: "network-only",
    });

    if (result.error) {
      console.error(result.error);
      return makeCollectionResponse([], JSON.stringify(result.error));
    }

    const rawMatters = result.data.searchMatters.items as unknown[];

    const matters = rawMatters.map(mapMatterFromGQL).filter(filterFalsy);

    return makeCollectionResponse(matters, null);
  } catch (error) {
    console.error(error);

    return makeCollectionResponse([], JSON.stringify(error));
  }
};

export const getMatterRetriever = () => ({
  myRecentMatters,
  getMatterById,
  getMattersById,
  search,
});

export type TMatterRetriever = ReturnType<typeof getMatterRetriever>;
